import { useState } from "react";
import { useInView } from "../../../hooks/useInView";
import { wrestlers, type Wrestler } from "../../../mocks/roster";

const statusStyles: Record<string, string> = {
  orange: "text-orange-600 bg-orange-50 border-orange-200",
  green: "text-green-600 bg-green-50 border-green-200",
  blue: "text-blue-600 bg-blue-50 border-blue-200",
  pink: "text-pink-600 bg-pink-50 border-pink-200",
  red: "text-red-600 bg-red-50 border-red-200",
};

function WrestlerModal({ wrestler, onClose }: { wrestler: Wrestler; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 animate-scale-in"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative bg-white max-w-3xl w-full overflow-hidden flex flex-col md:flex-row max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-gray-700 text-lg" />
        </button>

        {/* Image */}
        <div className="w-full md:w-72 flex-shrink-0 bg-gray-100 relative overflow-hidden" style={{ minHeight: "320px" }}>
          <img
            src={wrestler.image}
            alt={wrestler.name}
            className="w-full h-full object-cover object-top absolute inset-0"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          {/* Orange bottom strip */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-orange-500" />
        </div>

        {/* Content */}
        <div className="flex-1 p-6 md:p-8">
          <div className={`inline-flex items-center border text-xs font-condensed font-bold uppercase tracking-widest px-2 py-0.5 mb-4 ${statusStyles[wrestler.statusColor]}`}>
            {wrestler.status}
          </div>
          <h2 className="font-condensed font-black text-gray-900 text-3xl md:text-4xl uppercase tracking-tight mb-1">
            {wrestler.name}
          </h2>
          <p className="text-orange-500 font-condensed font-bold text-sm uppercase tracking-widest mb-5">
            &quot;{wrestler.nickname}&quot;
          </p>

          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            {wrestler.bio}
          </p>

          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Hometown", value: wrestler.hometown },
              { label: "Weight Class", value: wrestler.weightClass },
              { label: "Style", value: wrestler.style },
              { label: "Signature Move", value: wrestler.signature },
              { label: "Record (W-L-D)", value: wrestler.record },
            ].map((stat) => (
              <div key={stat.label} className="bg-gray-50 p-3 border border-gray-100 hover:border-orange-200 transition-colors duration-200">
                <p className="text-gray-400 font-condensed font-semibold text-xs uppercase tracking-widest mb-0.5">
                  {stat.label}
                </p>
                <p className="text-gray-900 font-condensed font-bold text-sm">
                  {stat.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WrestlerGrid() {
  const [selected, setSelected] = useState<Wrestler | null>(null);
  const { ref, inView } = useInView();

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="bg-white py-16 md:py-24 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-dot-grid-light pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className={`mb-12 anim-hidden ${inView ? "animate-fade-in-up" : ""}`}>
          <div className="inline-block border border-gray-300 text-gray-500 font-condensed font-semibold text-xs uppercase tracking-widest px-3 py-1 mb-4">
            {wrestlers.length} Competitors
          </div>
          <h2 className="font-condensed font-black text-gray-900 text-4xl md:text-5xl uppercase tracking-tight">
            Active Roster
          </h2>
          <div className={`mt-3 h-0.5 bg-orange-500 w-16 anim-hidden ${inView ? "line-grow delay-200" : ""}`} />
          <p className="text-gray-500 text-sm mt-3">Click a wrestler to view their full profile.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {wrestlers.map((w, i) => (
            <div
              key={w.id}
              onClick={() => setSelected(w)}
              className={`group cursor-pointer bg-white border border-gray-200 overflow-hidden hover:border-orange-400 hover:-translate-y-1 transition-all duration-300 anim-hidden ${inView ? "animate-fade-in-up" : ""}`}
              style={{ animationDelay: `${100 + i * 100}ms` }}
            >
              {/* Photo — 3:4 portrait */}
              <div className="relative w-full aspect-[3/4] overflow-hidden bg-gray-100">
                <img
                  src={w.image}
                  alt={w.name}
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  style={{ transition: "transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                {/* Orange bottom bar on hover */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                {/* View profile arrow */}
                <div className="absolute top-3 right-3 w-8 h-8 bg-orange-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0">
                  <i className="ri-user-search-line text-white text-sm" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-condensed font-black text-white text-xl uppercase tracking-wide group-hover:text-orange-400 transition-colors duration-200">
                    {w.name}
                  </h3>
                  <p className="text-orange-400 font-condensed font-semibold text-xs uppercase tracking-widest">
                    &quot;{w.nickname}&quot;
                  </p>
                </div>
              </div>

              {/* Info Bar */}
              <div className="p-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-gray-500 text-xs font-condensed uppercase tracking-wide">{w.weightClass}</p>
                  <p className="text-gray-700 text-xs">{w.hometown}</p>
                </div>
                <div className={`inline-flex items-center border text-xs font-condensed font-bold uppercase tracking-wider px-2 py-0.5 whitespace-nowrap ${statusStyles[w.statusColor]}`}>
                  {w.status}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <WrestlerModal wrestler={selected} onClose={() => setSelected(null)} />
      )}
    </section>
  );
}
